import { useEffect } from "react";

import { autorun } from "mobx";
import { observer } from "mobx-react-lite";
import posthog from "posthog-js";
import { PostHogProvider as PhProvider } from "posthog-js/react";

import { useAuth } from "@/store/AuthStoreProvider";

import { registerVia } from "./via";

import type { AuthModel } from "@/models/auth/AuthModel";
import type { UserData } from "@/store/user/userTypes";
import type { PostHogConfig } from "posthog-js";

declare global {
  interface Window {
    __POSTHOG_BOOTSTRAP__?: object;
  }
}

function initPosthog() {
  if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    const posthogParams: Partial<PostHogConfig> = {
      api_host: "/v1/session/sync",
      ui_host: "https://us.posthog.com",
      capture_dead_clicks: false,
      capture_exceptions: false,
      disable_surveys: true,
    };
    if (window.__POSTHOG_BOOTSTRAP__) {
      posthogParams.bootstrap = window.__POSTHOG_BOOTSTRAP__;
    }
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, posthogParams);
    registerVia();
    if (process.env.NODE_ENV === "development") {
      posthog.opt_out_capturing();
    }
  }
}

function initPosthogUser(auth: AuthModel) {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    return;
  }
  let prevUserProps: Pick<UserData, "username" | "name" | "email"> | null = null;

  return autorun(() => {
    const { currentUser } = auth;
    if (currentUser) {
      const { uid, username, name, email } = currentUser;
      if (posthog.get_distinct_id() !== uid) {
        posthog.identify(uid, { email, username, name });
      } else if (
        prevUserProps &&
        (username !== prevUserProps?.username || name !== prevUserProps?.name)
      ) {
        posthog.setPersonProperties({ username, name });
      }
      prevUserProps = { username, name, email };
    } else if (prevUserProps) {
      posthog.reset(true);
      posthog.reloadFeatureFlags();
      prevUserProps = null;
    }
  });
}

initPosthog();

/** @deprecated Exported for tests only, not for external use */
export const testInitPosthog = initPosthog;

export const PostHogProvider = observer(function PostHogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuth();

  useEffect(() => {
    return initPosthogUser(auth);
  }, [auth]);

  return <PhProvider client={posthog}>{children}</PhProvider>;
});
